/**
 * Per-node variable & format reference for the Flow Builder Info tab.
 * Keep in sync with wabaserver flowRuntime handlers and FLOW_NODES_REFERENCE.txt.
 */

const SYSTEM_VARS = [
  { name: "phone_number", desc: "Customer WhatsApp number" },
  { name: "last_user_message", desc: "Latest message text from the user" },
  { name: "customer_name", desc: "Profile name when available" },
  { name: "keyword", desc: "Matched trigger keyword" },
  { name: "message_id", desc: "Inbound message ID" },
  { name: "last_msg_timestamp", desc: "Last message time (UTC)" },
];

const VAR_NAMING = "Use lowercase_with_underscores (e.g. order_id, selected_option). In text fields write {{variable_name}}.";

/** @typedef {{ title: string, summary: string, creates?: string[], consumes?: string[], formats?: { label: string, example: string }[], connections?: string[], tips?: string[] }} NodeInfo */

/** @type {Record<string, NodeInfo>} */
export const FLOW_NODE_INFO = {
  trigger: {
    title: "Flow Trigger",
    summary: "Entry point — starts the flow when a user messages or matches keywords.",
    creates: ["Uses system variables only; does not create new variables."],
    consumes: [],
    formats: [
      { label: "Keywords", example: '["hi", "hello", "menu"]' },
      { label: "triggerType", example: "keyword | welcome | menu | default" },
      { label: "expectedTransferVariables", example: '["city_id", "budget_api"] — variables expected from upstream transfer' },
      { label: "allowAiHelpDuringConversation", example: "true — answer side questions mid-flow, then resume same step" },
      { label: "aiHelpKnowledgeBase", example: "FAQ text (max 4,000 characters) when AI help is on" },
    ],
    connections: ["One outgoing connection to the first step."],
    tips: [
      "Exactly one trigger per flow.",
      "Welcome/menu types ignore keyword list.",
      "Use Expected Transfer Variables to document what this flow needs from upstream flows.",
      "Use the AI Help button in the top toolbar (not this node) to configure mid-flow AI assistance.",
    ],
  },

  textMessage: {
    title: "Text Message",
    summary: "Sends a plain WhatsApp text message to the user.",
    creates: [],
    consumes: ["Any existing variable in message text via {{name}}."],
    formats: [{ label: "Message text", example: "Hi {{customer_name}}, your order {{order_id}} is ready." }],
    connections: ["Single default output."],
    tips: [VAR_NAMING, "Variables must be set by an earlier node (webhook, capture, AI, etc.)."],
  },

  captureText: {
    title: "Ask Question (Text)",
    summary: "Asks a question and saves the user's full reply as text — no AI parsing.",
    creates: ["variableName — stores the trimmed user message (default: selected)."],
    consumes: ["Optional {{vars}} inside the question text."],
    formats: [
      { label: "Stored value", example: "Plain string — exactly what the user typed." },
      { label: "Empty reply", example: "If only spaces, optional emptyAnswerMessage is sent and node waits again." },
    ],
    connections: ["Single default output after a valid answer."],
    tips: ["Use aiAskQuestion when you need AI to extract/format the answer."],
  },

  aiAskQuestion: {
    title: "AI Ask Question",
    summary: "Asks a question and uses AI to extract one answer (or multiple entities) into variable(s).",
    creates: [
      "variableName — main extracted answer (text/number/email/phone).",
      "entities[].variableName — optional extra fields from the same reply.",
    ],
    consumes: ["{{vars}} in question; aiPrompt describes what to extract."],
    formats: [
      { label: "answerType", example: "text | number | email | phone" },
      { label: "Example stored value", example: 'order_id = "ORD-9921"' },
    ],
    connections: ["Single default output after extraction."],
    tips: ["Write a clear aiPrompt: “Return only the order number, no extra text.”"],
  },

  webhook: {
    title: "Webhook",
    summary: "Calls an external HTTP API and maps JSON fields into flow variables.",
    creates: [
      "responseVariable — full JSON body (default: webhook_response).",
      "Each response mapping row → target variableName.",
    ],
    consumes: [
      "{{vars}} in requestBody and headers.",
      "inputVariable — optional primary input sent with the call.",
    ],
    formats: [
      { label: "Request body", example: '{"phone_number":"{{phone_number}}","order_id":"{{order_id}}"}' },
      { label: "Response path: data", example: "Stores the entire JSON body in the target variable." },
      { label: "Response path: .data", example: 'Body {"data":[...]} → maps the data property (array/object).' },
      { label: "Response path: data.status", example: 'Nested field → string/number at that path.' },
      { label: "Dynamic list source", example: '[{"name":"Plan A","desc":"₹99"},{"name":"Plan B","desc":"₹199"}]' },
    ],
    connections: ["Default continue, or branch per HTTP status handler."],
    tips: [
      "Use path data for full body; use .data when the list lives under a data key.",
      "For interactive list: map array to options, then listFromVariable = options, title field = name.",
    ],
  },

  interactiveList: {
    title: "Interactive List",
    summary: "Shows a WhatsApp list menu (max 10 rows). User picks one option.",
    creates: ["selectionVariable — stores the selected row title (e.g. selected_option)."],
    consumes: ["{{vars}} in header/body text.", "listFromVariable — array variable for dynamic rows."],
    formats: [
      { label: "Manual rows", example: "Up to 10 rows total across all sections." },
      { label: "Dynamic variable (array of objects)", example: '[{"name":"A","desc":"Option 1"},{"name":"B","desc":"Option 2"}]' },
      { label: "Field map", example: "title → name, description → desc (object keys in each item)." },
      { label: "Dynamic variable (strings only)", example: '["Option A","Option B","Option C"]' },
    ],
    connections: [
      "in_list — user picked a list row.",
      "not_in_list — user sent normal text instead of a list reply.",
    ],
    tips: [
      "After selection, use a Controller node on selectionVariable to branch by value.",
      "More than 10 options → split into multiple list nodes with “More options”.",
    ],
  },

  interactiveButtons: {
    title: "Interactive Buttons",
    summary: "Up to 3 quick-reply buttons on a message (legacy; prefer list for menus).",
    creates: ["selectionVariable — which button label was tapped."],
    consumes: ["{{vars}} in header/body/footer."],
    formats: [{ label: "Buttons", example: "Max 3 buttons; each needs id (e.g. cbtn_1) and text." }],
    connections: ["One output per button id + optional other."],
    tips: ["Connect each button handle to the correct next node."],
  },

  preApprovedTemplate: {
    title: "Pre-approved Template",
    summary: "Send a Meta-approved WhatsApp template with mapped flow variables.",
    creates: ["selectionVariable — stores quick-reply button text when user taps a button."],
    consumes: [
      "Map each template variable (header, body, URL button) to a flow variable.",
      "Optional header image/video/document override via URL or {{variable}}.",
    ],
    formats: [
      { label: "variableMappings", example: "body_1 → {{order_id}}, header_1 → {{customer_name}}" },
      { label: "templateSnapshot", example: "Cached components stored on save — no DB lookup at runtime." },
    ],
    connections: [
      "Quick-reply buttons → one route per button + Other fallback.",
      "URL / phone / copy-code buttons → single default output (no wait).",
    ],
    tips: [
      "Select an approved template from the dropdown — required variables appear automatically.",
      "URL and phone buttons never pause the flow; only quick-reply buttons wait for a reply.",
    ],
  },

  interactiveUrl: {
    title: "Interactive URL",
    summary: "Message with a button that opens a URL.",
    creates: [],
    consumes: ["{{vars}} in body and in url (e.g. https://track.example.com/{{order_id}})."],
    formats: [{ label: "URL", example: "Must be https://…; variables are replaced before send." }],
    connections: ["Single default output."],
    tips: [],
  },

  interactiveCarouselUrl: {
    title: "Carousel (URL)",
    summary: "2–10 cards; each card opens a URL.",
    creates: ["selectionVariable — optional; card interaction metadata."],
    consumes: ["{{vars}} in body.", "cardsVariable + cardFieldMap for dynamic cards."],
    formats: [
      { label: "Dynamic cards array", example: '[{"title":"Item","image_url":"https://...","link":"https://..."}]' },
      { label: "cardFieldMap", example: "Maps object keys → card body, mediaUrl, url, etc." },
    ],
    connections: ["Single default output."],
    tips: ["Minimum 2 cards, maximum 10."],
  },

  interactiveCarouselButton: {
    title: "Carousel (Buttons)",
    summary: "2–10 cards with reply buttons.",
    creates: ["selectionVariable — selected card/button info."],
    consumes: ["{{vars}} in body.", "Dynamic mode: cardsVariable + cardFieldMap."],
    formats: [{ label: "Cards", example: "2–10 cards; each with media + button label/payload." }],
    connections: ["Single default output."],
    tips: [],
  },

  condition: {
    title: "Condition",
    summary: "Single yes/no branch based on variable comparison.",
    creates: [],
    consumes: [
      "conditions[].variable — variable NAME without braces (e.g. order_status).",
      "conditions[].value — literal or {{var}} to compare against.",
    ],
    formats: [
      { label: "Operators", example: "equals, contains, greater, less" },
      { label: "Example", example: "variable: selected_option, operator: equals, value: Mumbai" },
    ],
    connections: ["true — condition met.", "false — condition not met."],
    tips: ["Use Controller when you have 3+ different values to route."],
  },

  controller: {
    title: "Controller",
    summary: "Routes to many branches by matching a variable against multiple values.",
    creates: [],
    consumes: [
      "branches[].variable — name without braces (e.g. selected_option).",
      "branches[].value — expected value for that branch.",
    ],
    formats: [{ label: "Example", example: "Branch when selected_option equals Mumbai → Mumbai path." }],
    connections: ["One output per branch id.", "default — no branch matched (recommended)."],
    tips: ["Ideal after interactiveList to route by selectionVariable."],
  },

  setVariable: {
    title: "Set Variable",
    summary: "Writes one or many variables in memory — no message sent.",
    creates: ["Each row’s variableName — target variable updated."],
    consumes: [
      "manual — text with {{vars}}; result is always a string.",
      "variable — copies raw value from sourceVariable (object/array preserved).",
    ],
    formats: [
      { label: "Manual", example: 'full_name = "{{first_name}} {{last_name}}"' },
      { label: "From variable", example: "Copy options from webhook_response into list_var." },
    ],
    connections: ["Single default output."],
    tips: ["Rows run in order; later rows can use values set earlier in the same node.", "Max 20 rows."],
  },

  codeMap: {
    title: "Code Transform",
    summary: "Run JavaScript to reshape any variable — return one object to set multiple outputs at once.",
    creates: [
      "outputVariables[] — each row is a flow variable your script can populate.",
      "Return { key: value } — keys must match output variable names.",
      "Return a single value (array/string/number) — saved to the first output only.",
    ],
    consumes: [
      "inputVariable — exposed as input in your code (optional; use get() for any variable).",
    ],
    formats: [
      {
        label: "Multiple outputs",
        example: "return { mapped_items: list.map(...), mapped_items_count: list.length };",
      },
      {
        label: "Single array",
        example: "return list.map(item => ({ id: item.id }));  // first output only",
      },
    ],
    connections: ["Usually after Webhook; before Carousel, List, Condition, or any node using the outputs."],
    tips: [
      "input = selected input variable (JSON auto-parsed).",
      "get('varName') = read any flow variable.",
      "vars = all flow variables as an object.",
      "Add multiple output rows — return an object with matching keys.",
    ],
  },

  jsonMap: {
    title: "Code Transform (legacy)",
    summary: "Legacy node type — same as Code Transform. Open and save to migrate to JavaScript.",
    creates: ["outputVariable"],
    consumes: ["inputVariable"],
    formats: [],
    connections: [],
    tips: ["Replace field mappings with JavaScript in the Code Transform editor."],
  },

  sendMedia: {
    title: "Send Media",
    summary: "Sends one or more image/video/document/audio messages.",
    creates: [],
    consumes: [
      "manual — each item has mediaUrl (URL or {{var}}).",
      "variable — variableName must hold object or array of media objects.",
    ],
    formats: [
      { label: "Single object", example: '{"type":"image","url":"https://...jpg","caption":"Hi"}' },
      { label: "Array", example: '[{"type":"document","url":"{{invoice_url}}","filename":"inv.pdf"}]' },
      { label: "Keys", example: "type, url|media_url|mediaUrl, caption?, filename?|fileName?" },
    ],
    connections: ["Single default output."],
    tips: ["Audio has no caption. One WhatsApp message per item."],
  },

  delay: {
    title: "Delay",
    summary: "Waits before continuing to the next node.",
    creates: [],
    consumes: [],
    formats: [{ label: "Duration", example: "duration: 5, unit: seconds | minutes | hours | days" }],
    connections: ["Single default output after wait."],
    tips: ["duration must be > 0."],
  },

  aiIntentClassifier: {
    title: "AI Intent Classifier",
    summary: "Classifies user message into intent labels using AI.",
    creates: ["fallbackVariable — matched intent label (e.g. intent_result)."],
    consumes: ["User’s last message (implicit)."],
    formats: [{ label: "Labels", example: '[{ name: "Track", value: "track_order" }]' }],
    connections: ["intent_<labelId> per label.", "intent_other — fallback."],
    tips: ["Connect every label handle plus intent_other."],
  },

  aiEntityExtractor: {
    title: "AI Entity Extractor",
    summary: "Extracts multiple entities from one user message.",
    creates: ["entities[].variableName — one variable per entity (e.g. user_city)."],
    consumes: ["User message text."],
    formats: [{ label: "Entity", example: '{ name: "City", type: "text", variableName: "user_city" }' }],
    connections: ["entity_found", "entity_not_found."],
    tips: [],
  },

  aiResponseGenerator: {
    title: "AI Response",
    summary: "Generates a free-form AI reply and sends it to the user.",
    creates: [],
    consumes: ["{{vars}} in prompt (e.g. {{last_user_message}}).", "Linked knowledgeBase content when attached."],
    formats: [{ label: "Prompt", example: "Answer helpfully about {{last_user_message}}" }],
    connections: ["Single default output."],
    tips: [],
  },

  aiKnowledgeBaseAnswer: {
    title: "AI Smart Assistant",
    summary: "Answers FAQ from a Knowledge Base and routes to connected flows when the user wants an action (e.g. book appointment).",
    creates: [
      "storeInVariable — last AI answer text.",
      "intentFallbackVariable — detected intent label when routing.",
    ],
    consumes: ["Linked knowledgeBase content.", "contextVariableNames for extra context."],
    formats: [
      { label: "intents[]", example: '{ value: "Book Appointment", keywords: ["book","visit"], description: "..." }' },
      { label: "sessionMode", example: "true — keep answering until maxQuestions" },
    ],
    connections: [
      "intent_<id> — one per intent route (connect to Transfer to Flow, etc.).",
      "answer / no_answer — session exit paths.",
    ],
    tips: [
      "Add keywords for instant routing without an LLM call (fastest).",
      "Connect each intent handle to Transfer to Flow for sub-flows.",
      "Do not use aiAskQuestion for FAQ — that node extracts data from a single question.",
    ],
  },

  knowledgeBase: {
    title: "Knowledge Base",
    summary: "Stores FAQ/content used by KB Answer nodes — not sent directly.",
    creates: [],
    consumes: [],
    formats: [{ label: "Content", example: "Plain text FAQ or attached files." }],
    connections: ["No runtime output — referenced by aiKnowledgeBaseAnswer."],
    tips: ["Attach to a KB Answer node via knowledgeBaseNodeId."],
  },

  template: {
    title: "Template Message",
    summary: "Sends an approved WhatsApp template (legacy in new flows).",
    creates: ["selectionVariable — for QUICK_REPLY button taps.", "body/header variables from template."],
    consumes: ["{{vars}} for template parameters and media headers."],
    formats: [{ label: "Body vars", example: "body_1, body_2 or named params per template." }],
    connections: ["Per QUICK_REPLY button id + other."],
    tips: ["Prefer textMessage / interactiveList for new flows."],
  },

  assignAgent: {
    title: "Assign Agent",
    summary: "Hands the chat to a human agent.",
    creates: [],
    consumes: [],
    formats: [{ label: "allocationMethod", example: "roundRobin" }],
    connections: ["Single default output."],
    tips: ["Select at least one agent in config."],
  },

  transferToFlow: {
    title: "Transfer to Flow",
    summary: "Jumps the user into another published flow.",
    creates: [],
    consumes: [],
    formats: [
      { label: "targetFlowId", example: "ID of the destination flow." },
      { label: "transferVariableMode", example: "all | selected | none" },
      { label: "transferVariables", example: '["city_id", "budget_api"] when mode is selected' },
    ],
    connections: ["Ends current flow path; starts target flow."],
    tips: [
      "targetFlowId is required.",
      "Use Selected variables to pass only the data the next flow needs.",
      "Session defaults (phone, name, keyword) are always set in the new flow.",
    ],
  },

  end: {
    title: "End Flow",
    summary: "Stops flow execution on this path.",
    creates: [],
    consumes: [],
    formats: [],
    connections: ["No outgoing connections."],
    tips: ["Use at the end of every branch."],
  },
};

/**
 * @param {string} nodeType
 * @returns {NodeInfo}
 */
export function getFlowNodeInfo(nodeType) {
  return (
    FLOW_NODE_INFO[nodeType] || {
      title: nodeType || "Node",
      summary: "Configure this step in the flow.",
      tips: [VAR_NAMING],
    }
  );
}

export { SYSTEM_VARS, VAR_NAMING };
